import { CharacterHealth } from '@/types/adventures';

interface HealthBarProps {
    health: CharacterHealth;
    name?: string;
    showValues?: boolean;
    size?: 'sm' | 'md' | 'lg';
}

/**
 * HealthBar component displays a character's health as a visual progress bar.
 * Color-coded: green (>70%), yellow (30-70%), red (<30%), gray (incapacitated)
 */
export function HealthBar({
    health,
    name,
    showValues = true,
    size = 'md',
}: HealthBarProps) {
    const percentage = health.health_percentage;

    // Determine color based on health percentage
    const getBarColor = () => {
        if (health.is_incapacitated) return 'bg-gray-500';
        if (percentage > 70) return 'bg-green-500';
        if (percentage > 30) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    // Determine text color for the label
    const getTextColor = () => {
        if (health.is_incapacitated) return 'text-gray-400';
        if (percentage > 70) return 'text-green-400';
        if (percentage > 30) return 'text-yellow-400';
        return 'text-red-400';
    };

    // Size classes
    const heightClass = {
        sm: 'h-2',
        md: 'h-3',
        lg: 'h-4',
    }[size];

    const textClass = {
        sm: 'text-xs',
        md: 'text-sm',
        lg: 'text-base',
    }[size];

    return (
        <div className="w-full">
            {/* Header with name and values */}
            {(name || showValues) && (
                <div className={`flex justify-between items-center mb-1 ${textClass}`}>
                    {name && (
                        <span className={`font-medium ${health.is_incapacitated ? 'text-gray-500 line-through' : 'text-gray-200'}`}>
                            {name}
                            {health.is_incapacitated && <span className="ml-2 text-gray-500 italic">(Incapacitated)</span>}
                        </span>
                    )}
                    {showValues && (
                        <span className={getTextColor()}>
                            {health.current_health}/{health.max_health}
                        </span>
                    )}
                </div>
            )}

            {/* Health bar */}
            <div className={`w-full bg-slate-700 rounded-full ${heightClass} overflow-hidden`}>
                <div
                    className={`${heightClass} ${getBarColor()} transition-all duration-300 rounded-full`}
                    style={{ width: `${Math.max(0, Math.min(100, percentage))}%` }}
                />
            </div>
        </div>
    );
}

interface CompactHealthBarProps {
    health: CharacterHealth;
}

/**
 * Compact inline health bar for use in lists/tables.
 */
export function CompactHealthBar({ health }: CompactHealthBarProps) {
    const percentage = health.health_percentage;

    const getBarColor = () => {
        if (health.is_incapacitated) return 'bg-gray-500';
        if (percentage > 70) return 'bg-green-500';
        if (percentage > 30) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    const getTextColor = () => {
        if (health.is_incapacitated) return 'text-gray-500';
        if (percentage > 70) return 'text-green-400';
        if (percentage > 30) return 'text-yellow-400';
        return 'text-red-400';
    };

    return (
        <div className="flex items-center gap-2">
            <div className="w-16 bg-slate-700 rounded-full h-2 overflow-hidden">
                <div
                    className={`h-2 ${getBarColor()} rounded-full`}
                    style={{ width: `${percentage}%` }}
                />
            </div>
            <span className={`text-xs ${getTextColor()}`}>
                {health.is_incapacitated ? 'KO' : `${health.current_health}/${health.max_health}`}
            </span>
        </div>
    );
}

export default HealthBar;
